import React, { useRef, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { openFlyout } from 'actions/flyout';
import { showModal } from 'actions/modal';
import { showCreateProjectModal } from 'actions/projects';

import { useCanUser } from 'shared-hooks/permission';
import { canCreateProject } from 'services/plan-service';

import { AlertContext } from 'context/AlertContext';

import Popout from 'components/popout';
import PopoutListActions from 'components/PopoutListActions';

import IconPlus from 'icons/icon-plus';
import IconProject from 'icons/icon-project';
import IconTask from 'icons/icon-task';
import IconDoc from 'icons/icon-doc';

const AppHeadCreateButton = () => {
    const { t } = useTranslation(['common', 'project', 'task', 'doc']);
    const alertCtx = useContext(AlertContext);
    const popoutCtrl = useRef(null);
    const userCanCreateProject = useCanUser('createProject');
    const userCanCreateTask = useCanUser('createTask');
    const userCanCreateDoc = useCanUser('createDoc');

    const actions = useMemo(() => {
        const result = [];

        if (userCanCreateProject) result.push('createProject');
        result.push('createPersonalTask');
        if (userCanCreateTask) result.push('createTask');
        if (userCanCreateDoc) result.push('createDoc');
        result.push('createNote');

        return result;
    }, [userCanCreateProject, userCanCreateTask, userCanCreateDoc]);

    const handleCreateProjectClick = () => {
        if (canCreateProject()) {
            showCreateProjectModal();
        } else {
            alertCtx?.showQuotaAlert('projects');
        }

        popoutCtrl.current?.hide();
    }

    const handleCreatePersonalTaskClick = () => {
        openFlyout('personalTodos', {
            position: 'right'
        });

        popoutCtrl.current?.hide();
    }

    const handleCreateTaskClick = () => {
        showModal('CREATE_TASK');

        popoutCtrl.current?.hide();
    }

    const handleCreateDocClick = () => {
        showModal('DOC_CREATE');

        popoutCtrl.current?.hide();
    }

    const handleCreateNoteClick = () => {
        showModal('DOC_CREATE', {
            isPersonal: true
        });

        popoutCtrl.current?.hide();
    }

    const renderAction = actionId => {
        switch (actionId) {
            case 'createProject':
                return (
                    <div className="item-inner" onClick={handleCreateProjectClick}>
                        <span className="item-icon"><IconProject /></span>

                        { t('project:Project') }
                    </div>
                );

            case 'createPersonalTask':
                return (
                    <div className="item-inner" onClick={handleCreatePersonalTaskClick}>
                        <span className="item-icon"><IconTask /></span>

                        { t('task:Personal Task') }
                    </div>
                );

            case 'createTask':
                return (
                    <div className="item-inner" onClick={handleCreateTaskClick}>
                        <span className="item-icon"><IconTask /></span>

                        { t('task:Project Task') }
                    </div>
                );

            case 'createDoc':
                return (
                    <div className="item-inner" onClick={handleCreateDocClick}>
                        <span className="item-icon"><IconDoc /></span>

                        { t('doc:Document') }
                    </div>
                );

            case 'createNote':
                return (
                    <div className="item-inner" onClick={handleCreateNoteClick}>
                        <span className="item-icon"><IconDoc /></span>

                        { t('doc:Note') }
                    </div>
                );
        }
    }

    const handleTriggerClick = () => {
        popoutCtrl.current?.show();
    };

    return (
        <button className="app-bar-btn btn btn-success btn-xsmall" style={{ fontSize: 13 }} onClick={handleTriggerClick}>
            <IconPlus size="12px" /> { t('Create') }

            <Popout
                className="xxxxsmall"
                xAlign="left"
                provideController={popoutCtrl}
             >
                <PopoutListActions actions={actions} size="large" renderAction={renderAction} updatePopoutPosition={popoutCtrl.current?.updatePosition} />
            </Popout>
        </button>
    );
}

export default AppHeadCreateButton;
